/**
 * WYSIWYG kontentini xavfsiz ko'rsatish (XSS himoyasi, ikkinchi qatlam).
 *
 * Backend (`apps.portal.services.content.sanitize_html`) HTML'ni saqlashda
 * tozalaydi; brauzerda esa oq ro'yxat asosida yana bir marta o'tkaziladi:
 * faqat matn formatlash teglari va xavfsiz havolalar qoladi, skript, uslub,
 * hodisa atributlari (`on*`) va `javascript:` manzillar olib tashlanadi.
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "b", "strong", "i", "em", "u", "s", "h2", "h3", "h4", "ul", "ol", "li",
  "a", "blockquote", "table", "thead", "tbody", "tr", "th", "td", "img", "span", "hr",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "target", "rel"]),
  img: new Set(["src", "alt", "width", "height"]),
  th: new Set(["colspan", "rowspan"]),
  td: new Set(["colspan", "rowspan"]),
};

const SAFE_URL = /^(https?:|mailto:|tel:|\/(?!\/)|#)/i;

function cleanNode(node: Element, doc: Document): void {
  for (const child of Array.from(node.children)) {
    const tag = child.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      /* Ruxsat etilmagan teg: skript/uslub butunlay o'chiriladi, qolganlarining
         matni saqlanadi (masalan <div> → ichidagi matn). */
      if (["script", "style", "iframe", "object", "embed", "form", "svg", "math"].includes(tag)) {
        child.remove();
        continue;
      }
      const fragment = doc.createDocumentFragment();
      while (child.firstChild) fragment.appendChild(child.firstChild);
      child.replaceWith(fragment);
      cleanNode(node, doc);
      return;
    }
    const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
    for (const attribute of Array.from(child.attributes)) {
      const name = attribute.name.toLowerCase();
      if (!allowed.has(name)) {
        child.removeAttribute(attribute.name);
        continue;
      }
      if ((name === "href" || name === "src") && !SAFE_URL.test(attribute.value.trim())) {
        child.removeAttribute(attribute.name);
      }
    }
    if (tag === "a" && child.getAttribute("target") === "_blank") {
      child.setAttribute("rel", "noreferrer noopener");
    }
    if (tag === "img") {
      child.setAttribute("loading", "lazy");
      if (!child.getAttribute("alt")) child.setAttribute("alt", "");
    }
    cleanNode(child, doc);
  }
}

export function sanitizeHtml(html: string): string {
  if (!html) return "";
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  cleanNode(doc.body, doc);
  return doc.body.innerHTML;
}

/** HTML'dan sof matn (qisqa tavsif va aria-label uchun). */
export function htmlToText(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html ?? "";
  return new DOMParser().parseFromString(html, "text/html").body.textContent?.trim() ?? "";
}
